import React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

/**
 * FormSection — Enterprise Design System
 *
 * Groups related form fields under a titled section with optional description.
 * Renders as a card-like container with consistent spacing.
 *
 * Props:
 *  - title: string — section heading rendered in subtitle2 typography
 *  - description: string (optional) — explanatory text below the title
 *  - children: ReactNode — form fields rendered below
 *  - card: boolean (default: true) — wrap in a card-like container
 */
function FormSection({ title, description, children, card = true }) {
  const content = (
    <>
      <Typography variant="subtitle2" sx={{ fontSize: '0.8125rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.02em' }}>
        {title}
      </Typography>
      {description && (
        <Typography variant="body2" color="text.secondary" sx={{ display: 'block', mt: 0.5, lineHeight: 1.5 }}>
          {description}
        </Typography>
      )}
      <Box sx={{ mt: 2.5 }}>
        {children}
      </Box>
    </>
  );

  if (!card) {
    return <Box sx={{ mb: 3 }}>{content}</Box>;
  }

  return (
    <Box
      sx={{
        mb: 3,
        p: { xs: 1.5, sm: 2.5 },
        borderRadius: '12px',
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper',
      }}
    >
      {content}
    </Box>
  );
}

export default FormSection;
